import * as React from 'react';

import {
  DescriptionPage_ProductVariant as ProductVariant,
  DescriptionPage_UpsellType as UpsellType,
} from '__generated__/graphql-types';

import KeyInformationContentCount from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationContentCount';
import KeyInformationCourseSatisfactionScore from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationCourseSatisfactionScore';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import KeyInformationDifficultyItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDifficultyItem';
import KeyInformationEarnCertificate from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationEarnCertificate';
import KeyInformationHorizontalCourseDurationItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalCourseDurationItem';
import KeyInformationHorizontalReviewsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalReviewsItem';
import KeyInformationProjectDuration from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationProjectDuration';
import KeyInformationProjectProductType from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationProjectProductType';
import KeyInformationReviewsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationReviews';
import KeyInformationSpecializationDuration from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationSpecializationDuration';
import KeyInformationStakability from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationStackability';
import LearnMoreLink from 'bundles/unified-description-page-common/components/shared/LearnMoreLink';
import {
  MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS,
  MAX_NUMBER_OF_KIM_ITEMS,
  PROJECTS_LEARN_ANCHOR_ID,
} from 'bundles/unified-description-page-common/constants';
import type PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import { KeyInformationItemNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import type { KeyInfoModule } from 'bundles/unified-description-page-common/types/moduleConfig';
import combineCourseDurationAndSchecule from 'bundles/unified-description-page-common/utils/combineCourseDurationAndSchecule';
import combineDurationAndSchedule from 'bundles/unified-description-page-common/utils/combineDurationAndSchedule';
import { getKeyInformationItemsToRender } from 'bundles/unified-description-page-common/utils/getKeyInformationItemsToRender';
import {
  getKeyInformationModuleSubtitle,
  getKeyInformationModuleSubtitleForProject,
} from 'bundles/unified-description-page-common/utils/getKeyInformationModuleTitle';
import getProjectPageTypeName from 'bundles/unified-description-page-common/utils/getProjectPageTypeName';

import _t from 'i18n!nls/xdp';

export const getConsumerSpecializationKeyInformationItems = (
  pageData: ConsumerSpecializationPageFieldsFragment,
  keyInformationModuleConfig?: KeyInfoModule,
  disableCta?: boolean,
  maxNumberOfHorizontalKimItems: number = MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS,
  showEnrollmentCountOnReviewsItem: boolean = false,
  showSubscriptionFirstXdpUpdates?: boolean | undefined,
  enableXDPRedesign?: boolean
) => {
  if (
    !keyInformationModuleConfig ||
    !keyInformationModuleConfig.items ||
    keyInformationModuleConfig.items.length === 0
  ) {
    return { items: [] };
  }

  let items = keyInformationModuleConfig.items.map((config) => {
    switch (config.name) {
      case KeyInformationItemNames.Difficulty:
        if (pageData.difficultyLevel) {
          return (
            <KeyInformationDifficultyItem
              difficultyLevel={pageData.difficultyLevel}
              cmlRecommendedBackground={pageData.cmlRecommendedBackground}
              numSubtitleLines={2}
              key={KeyInformationItemNames.Difficulty}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Duration:
        if (typeof pageData?.totalLearningMonths === 'number' && typeof pageData?.learningHoursPerWeek === 'number') {
          return (
            <KeyInformationSpecializationDuration
              displayAsHeaderAndSubtitle={true}
              totalLearningMonths={pageData?.totalLearningMonths}
              learningHoursPerWeek={pageData?.learningHoursPerWeek}
              key={config.name}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Schedule:
        return (
          <KeyInformationDetailsItem
            title={_t('Flexible schedule')}
            subtitle={_t('Learn at your own pace')}
            key={config.name}
          />
        );
      case KeyInformationItemNames.Reviews:
        if (pageData?.ratings?.averageFiveStarRating) {
          return (
            <KeyInformationHorizontalReviewsItem
              productRating={pageData?.ratings?.averageFiveStarRating}
              totalReviewCount={pageData?.ratings?.ratingCount}
              key={config.name}
              enrollmentCount={showEnrollmentCountOnReviewsItem ? pageData.totalEnrollmentCount : null}
              isSpecialization
            />
          );
        }
        return null;
      case KeyInformationItemNames.Stackability:
        if (pageData.productTags?.linkToACECreditRecommendation || (pageData.degrees && pageData.degrees.length > 0)) {
          return <KeyInformationStakability degrees={pageData?.degrees} key={config.name} disableCta={disableCta} />;
        }
        return null;
      case KeyInformationItemNames.ContentCount:
        return (
          <KeyInformationContentCount
            courseCount={pageData.courses?.length ?? 0}
            subtitle={getKeyInformationModuleSubtitle(pageData.productVariant)}
            key={KeyInformationItemNames.ContentCount}
          />
        );
      case KeyInformationItemNames.EarnCertificate:
        if (showSubscriptionFirstXdpUpdates || enableXDPRedesign) {
          return <KeyInformationEarnCertificate key={KeyInformationItemNames.EarnCertificate} />;
        }
        return null;
      default:
        return <KeyInformationDetailsItem title={config.title} subtitle={config.subtitle} key={config.name} />;
    }
  });

  items = items.filter((item) => item !== null);

  const degree = pageData.degrees?.[0];
  const productVariant = pageData.productVariant;
  /**
   * Combine duration and schedule items into one item for horizontal display if:
   * 1. Professional Certificate or
   * 2. Specialization which is OFFERS_CREDIT upsell type
   */
  if (
    productVariant !== ProductVariant.StandardSpecialization ||
    (productVariant === ProductVariant.StandardSpecialization && degree?.upsellType === UpsellType.OffersCredit)
  ) {
    items = combineDurationAndSchedule({
      items,
      totalLearningMonths: pageData?.totalLearningMonths,
      learningHoursPerWeek: pageData?.learningHoursPerWeek,
    });
  }

  if (showSubscriptionFirstXdpUpdates) {
    const { itemsToRender, keyInformationIncludesReview, shouldRenderStackabilityInDetailsSection } =
      getKeyInformationItemsToRender({
        items,
        totalLearningMonths: pageData?.totalLearningMonths,
        learningHoursPerWeek: pageData?.learningHoursPerWeek,
      });

    return {
      items: itemsToRender,
      keyInformationIncludesReview,
      shouldRenderStackabilityInDetailsSection,
    };
  }
  // The horizontal KIM only supports a maximum of 5 items.
  items = items.slice(0, maxNumberOfHorizontalKimItems);

  return {
    items,
  };
};

export const getConsumerProjectKeyInformationItems = (
  pageData: ConsumerCoursePageFieldsFragment,
  keyInformationModuleConfig?: KeyInfoModule,
  disableCta?: boolean,
  pageType?: (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES],
  showEnrollmentCountOnReviewsItem: boolean = false,
  showSubscriptionFirstXdpUpdates?: boolean | undefined
) => {
  if (
    !keyInformationModuleConfig ||
    !keyInformationModuleConfig.items ||
    keyInformationModuleConfig.items.length === 0
  ) {
    return { items: [] };
  }

  let items = keyInformationModuleConfig.items.map((config) => {
    switch (config.name) {
      case KeyInformationItemNames.Difficulty:
        if (pageData.difficultyLevel) {
          return (
            <KeyInformationDifficultyItem
              difficultyLevel={pageData.difficultyLevel}
              cmlRecommendedBackground={pageData.cmlRecommendedBackground}
              key={KeyInformationItemNames.Difficulty}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Duration:
        return (
          <KeyInformationProjectDuration
            estimatedWorkload={pageData?.estimatedWorkload}
            key={config.name}
            pageType={pageType}
          />
        );
      case KeyInformationItemNames.LearningMethod:
        return (
          <KeyInformationDetailsItem
            title={_t('Hands-on learning')}
            subtitle={
              !disableCta && (
                <LearnMoreLink
                  trackingName="kim_hands_on_learning_link"
                  ariaLabel={_t('Learn more about hands-on learning')}
                  href={`#${PROJECTS_LEARN_ANCHOR_ID}`}
                />
              )
            }
          />
        );
      case KeyInformationItemNames.Reviews:
        if (pageData?.ratings?.averageFiveStarRating) {
          return (
            <KeyInformationReviewsItem
              productRating={pageData?.ratings?.averageFiveStarRating}
              totalReviewCount={pageData?.ratings?.ratingCount}
              key={config.name}
              enrollmentCount={showEnrollmentCountOnReviewsItem ? pageData.totalEnrollmentCount : null}
            />
          );
        }
        return null;
      case KeyInformationItemNames.ProductType:
        return (
          <KeyInformationProjectProductType
            title={getProjectPageTypeName(pageType)}
            subtitle={getKeyInformationModuleSubtitleForProject(pageType)}
            key={config.name}
          />
        );
      case KeyInformationItemNames.EarnCertificate:
        if (showSubscriptionFirstXdpUpdates) {
          return <KeyInformationEarnCertificate key={KeyInformationItemNames.EarnCertificate} />;
        }
        return null;
      default:
        return <KeyInformationDetailsItem title={config.title} subtitle={config.subtitle} key={config.name} />;
    }
  });

  items = items.filter((item) => item !== null);

  if (showSubscriptionFirstXdpUpdates) {
    const { itemsToRender, keyInformationIncludesReview, shouldRenderStackabilityInDetailsSection } =
      getKeyInformationItemsToRender({
        items,
      });

    return {
      items: itemsToRender,
      keyInformationIncludesReview,
      shouldRenderStackabilityInDetailsSection,
    };
  }

  return {
    items,
  };
};

export const getConsumerCourseKeyInformationItems = (
  pageData: ConsumerCoursePageFieldsFragment,
  keyInformationModuleConfig?: KeyInfoModule,
  disableCta?: boolean,
  maxNumberOfKimItems: number = MAX_NUMBER_OF_KIM_ITEMS,
  showEnrollmentCountOnReviewsItem: boolean = false,
  showSubscriptionFirstXdpUpdates?: boolean | undefined
) => {
  if (
    !keyInformationModuleConfig ||
    !keyInformationModuleConfig.items ||
    keyInformationModuleConfig.items.length === 0
  ) {
    return { items: [] };
  }

  let reviewItemIndex = -1;
  let hasCourseSatisfactionScoreItemConfig = false;

  let items = keyInformationModuleConfig.items.map((config, index) => {
    switch (config.name) {
      case KeyInformationItemNames.Reviews:
        if (pageData?.ratings?.averageFiveStarRating) {
          reviewItemIndex = index;
          return (
            <KeyInformationHorizontalReviewsItem
              productRating={pageData?.ratings?.averageFiveStarRating}
              totalReviewCount={pageData?.ratings?.ratingCount}
              key={config.name}
              enrollmentCount={showEnrollmentCountOnReviewsItem ? pageData.totalEnrollmentCount : null}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Difficulty:
        if (pageData.difficultyLevel) {
          return (
            <KeyInformationDifficultyItem
              difficultyLevel={pageData.difficultyLevel}
              cmlRecommendedBackground={pageData.cmlRecommendedBackground}
              key={config.name}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Duration:
        if (pageData.material?.totalDuration) {
          return (
            <KeyInformationHorizontalCourseDurationItem
              totalDuration={pageData.material?.totalDuration}
              key={config.name}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Schedule:
        return (
          <KeyInformationDetailsItem
            title={_t('Flexible schedule')}
            subtitle={_t('Learn at your own pace')}
            key={config.name}
          />
        );
      case KeyInformationItemNames.Stackability:
        if (pageData.degrees && pageData.degrees.length > 0) {
          return <KeyInformationStakability degrees={pageData?.degrees} key={config.name} disableCta={disableCta} />;
        }
        return null;
      case KeyInformationItemNames.ContentCount:
        if (!pageData.material?.weeks.length) return null;
        return (
          <KeyInformationContentCount
            moduleCount={pageData.material.weeks.length}
            key={KeyInformationItemNames.ContentCount}
          />
        );
      case KeyInformationItemNames.CourseSatisfactionScore:
        hasCourseSatisfactionScoreItemConfig = true;
        if (!pageData.averageContentSatisfactionScore) return null;
        return (
          <KeyInformationCourseSatisfactionScore
            key={KeyInformationItemNames.CourseSatisfactionScore}
            averageContentSatisfactionScore={pageData.averageContentSatisfactionScore}
          />
        );
      case KeyInformationItemNames.EarnCertificate:
        if (showSubscriptionFirstXdpUpdates) {
          return <KeyInformationEarnCertificate key={KeyInformationItemNames.EarnCertificate} />;
        }
        return null;
      default:
        return <KeyInformationDetailsItem title={config.title} subtitle={config.subtitle} key={config.name} />;
    }
  });

  items = items.filter((item) => item !== null);

  if (showSubscriptionFirstXdpUpdates) {
    const { itemsToRender, keyInformationIncludesReview, shouldRenderStackabilityInDetailsSection } =
      getKeyInformationItemsToRender({
        items,
        reviewItemIndex,
        totalDuration: pageData.material?.totalDuration,
        productRating: pageData?.ratings?.averageFiveStarRating,
        totalReviewCount: pageData?.ratings?.ratingCount,
        enrollmentCount: showEnrollmentCountOnReviewsItem ? pageData.totalEnrollmentCount : null,
        satisfactionScore: pageData.averageContentSatisfactionScore,
      });

    return {
      items: itemsToRender,
      keyInformationIncludesReview,
      shouldRenderStackabilityInDetailsSection,
    };
  }

  // In showHorizontalKIMOnXDP treatment variant
  if (hasCourseSatisfactionScoreItemConfig) {
    // Combine duration and schedule items if exceed MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS
    if (items.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS) {
      items = combineCourseDurationAndSchecule({
        items,
        totalDuration: pageData.material?.totalDuration,
      });
    }
    // The horizontal KIM only supports a maximum of 5 items.
    return {
      items: items.slice(0, MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS),
      keyInformationIncludesReview: false,
    };
  }

  // Standalone course in showHorizontalKIMOnXDP control variant
  // The horizontal KIM only supports a maximum of 4 items.
  // If there are more than 4 items, we remove the reviews item from KIM.
  // Reviews will be displayed in the right side of the Hero module.
  if (items.length > maxNumberOfKimItems && reviewItemIndex !== -1) {
    items.splice(reviewItemIndex, 1);
    return {
      items,
      keyInformationIncludesReview: false,
    };
  }

  return {
    items,
    keyInformationIncludesReview: reviewItemIndex !== -1,
  };
};
